<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    v-loading="processing"
    :visible.sync="visible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Two-Factor Authentication (2FA)</h1>
        <div class="modal-actions col-2">
          <el-button type="default" @click="cancelOperation"> Cancel</el-button>
        </div>
      </div>
    </div>

    <form @submit.prevent="verify">
      <h2 class="form-section-header">Setup Authenticator App</h2>
      <p class="black-text">
        Authenticator apps and browser extensions like
        <a
          class="ml-5"
          href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
          target="_blank"
          >Google Authenticator</a
        >,
        <a
          class="ml-5"
          href="https://support.1password.com/one-time-passwords/"
          target="_blank"
          >1Password</a
        >,
        <a
          href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
          target="_blank"
          >Microsoft Authenticator </a
        >, etc. generate one-time passwords that are used as a second factor to
        verify your identity when prompted during sign-in.
      </p>
      <h2 class="form-section-header">Scan the QR Code</h2>
      <p class="black-text">Use an authenticator app or browser extension to scan.</p>
      <img :src="qrcode" width="180" height="180" alt="QRCode Url" />
      <p class="black-text">
        Unable to scan? Use the secret key to manually configure your
        authenticator app.
      </p>
      <p class="black-text mt-0">
        Secret Key: <span style="font-weight: 600">{{ secret }}</span>
      </p>

      <h2 class="form-section-header">Verify the code from the app</h2>
      <div class="inline-form-group">
        <el-col :span="12">
          <el-input
            ref="code"
            v-model="otp"
            placeholder="XXXXXX"
            maxlength="6"
            v-mask="'######'"
            v-loading="verifying"
          >
            <template slot="prefix">Code</template>
          </el-input>
        </el-col>
      </div>
    </form>
  </el-dialog>
</template>

<script>
import { apiV2Host } from "@/feathers/feathers-client";

export default {
  name: "MfaApp",

  props: {
    visible: { required: true },
  },

  data() {
    return {
      processing: false,
      verifying: false,
      otp: "",
      secret: "",
      qrcode: "",
    };
  },


  watch: {
    otp() {
      if (this.otp.length === 6) {
        this.verify();
      }
    },

    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.code.focus();
        });
      }
    }
  },

  computed: {
    headers() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
        Accept: "application/json",
      };
    },

    formValid() {
      return this.otp && this.otp.length === 6;
    },
  },

  methods: {
    cancelOperation() {
      this.otp = "";
      this.$emit("cancelOperation");
    },

    notifyGenericError() {
      this.$notify({
        title: "Something Went Wrong!",
        message: "Please try again later!",
        type: "error",
      });
    },

    errorOnApiCall(json = {}, notify = true) {
      const { code, name, message } = json;
      if (code && ["4", "5"].includes(`${code}`[0])) {
        if (notify) {
          this.$notify({
            title: name,
            message: message,
            type: "error",
          });
        }
        return true;
      }

      return false;
    },

    async refreshUserInfo() {
      try {
        await this.$store.dispatch("auth/authenticate", {
          strategy: "jwt",
          accessToken: this.$store.state.auth.accessToken,
        });
      } catch (_e) {
        //
      }
    },

    async verify() {
      const body = {
        otp: this.otp,
        mode: "app",
        secret: this.secret,
      };
      this.verifying = true;

      try {
        const res = await fetch(`${apiV2Host}/mfa/auth/verify`, {
          method: "POST",
          body: JSON.stringify(body),
          headers: this.headers,
        });

        const json = await res.json();

        if (this.errorOnApiCall(json)) {
          this.verifying = false;
          this.otp = "";
          return;
        }

        await this.refreshUserInfo();

        this.verifying = false;
        this.otp = "";

        this.$notify({
          title: "Success",
          message: "Successfully configured Authenticator App!",
          type: "success",
        });

        this.$emit("verified");
      } catch (_e) {
        this.notifyGenericError();
        this.verifying = false;
      }
    },
  },

  async created() {
    const body = {
      mode: "app",
    };

    try {
      this.processing = true;
      const res = await fetch(`${apiV2Host}/mfa/auth/setup`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: this.headers,
      });
      const json = await res.json();
      const { secret, qrcode } = json;
      this.secret = secret;
      this.qrcode = qrcode;
      this.processing = false;
    } catch (_e) {
      this.processing = false;
    }
  },
};
</script>

<style lang="scss" scoped>
form {
  max-width: 600px;
  margin: 0 auto;

  @media only screen and (max-width: 1105px) {
    margin-top: 60px;
  }

  @media only screen and (max-width: 767px) {
    margin-top: 0;
  }
}
</style>
